import type { StatusBody } from "odyssey-shared";
import type { ServerNoticeTone } from "../ui/kit/ServerNotice.tsx";
import { say } from "../words.ts";
import { queueEstimate } from "./queue/estimate-words.ts";

/**
 * What the entry screens make of the gateway's status (ADR-078 decisions 2, 3 and 7), pure and moved by probes. A
 * probe that got no answer is unreachable; two in a row are offline, and the first answer of any kind ends it, so
 * one lost probe, or a gateway before `/status` that answers 404, never locks a player out. A body's times are fixed
 * at receipt on the page's clock, so a wrong clock cannot move them. The strip (`serverNotice`) and the maintenance
 * page (`maintenancePage`) say what holds now; every word is in `SERVER_STATUS_WORDING` and is a draft for the author.
 * A busy server is one whose logins wait in the login queue (ADR-127 decision 8): its strip says the world is full
 * and, when the status carries `waitMs`, the estimated wait in the queue window's words; Log in stays enabled.
 *
 * While the gateway cannot be reached, a probe may carry an operator's notice read from the status watcher outside
 * the site (ADR-078's amendment of 2026-10-13, P1.13-T3): the offline strip then says it in place of its own
 * sentence. The newest probe decides, and any answer of the gateway's forgets it.
 */

/**
 * What one probe found: an answer with a body the page reads; an answer it cannot read (a 404 from a gateway before
 * `/status`, a 429, a 5xx, an odd body), which is no news but proves the gateway alive; no answer at all, with the
 * operator's notice when the status watcher gave one for an offline gateway; or the player acting after "The server
 * is back.", which clears it.
 */
export type Probe = { readonly kind: "answer"; readonly body: StatusBody } | { readonly kind: "no-news" } | { readonly kind: "unreachable"; readonly notice?: string } | { readonly kind: "acknowledged" };

/**
 * The page's view of the server: the probes in a row that got no answer; the last body read and the page time it
 * was read at; what the first answer said, which sends a fresh visit during maintenance straight to the maintenance
 * page; whether the server has been offline or under maintenance since it was last open; whether it is back
 * from that, until the player acts; and the operator's notice the newest probe with no answer carried, or null.
 */
export interface ServerView {
  readonly failures: number;
  readonly body: StatusBody | null;
  readonly readAt: number;
  readonly opening: "maintenance" | "other" | null;
  readonly troubled: boolean;
  readonly restored: boolean;
  readonly notice: string | null;
}

/**
 * Where the server stands for the entry screens, in the order a strip shows them.
 */
export type ServerPhase = "unknown" | "offline" | "maintenance" | "soon" | "busy" | "restored" | "open";

/**
 * The view before the first probe.
 */
export const INITIAL_SERVER_VIEW: ServerView = { failures: 0, body: null, readAt: 0, opening: null, troubled: false, restored: false, notice: null };

/**
 * Probes in a row with no answer before the page says offline and blocks Log in.
 */
export const OFFLINE_AFTER_FAILURES = 2;

/**
 * The wait between probes while the answer is steady.
 */
export const STEADY_PROBE_MS = 30_000;

/**
 * The wait between probes while the server says it is closed for maintenance: the maintenance page asks in the
 * background and offers nothing to press, so pages waiting out a maintenance ask half as often as an open server's.
 */
export const MAINTENANCE_PROBE_MS = 60_000;

/**
 * The waits after the first, second and third probe in a row with no answer; after that, `OFFLINE_PROBE_MS`.
 */
const BACKOFF_MS = [2_000, 4_000, 8_000] as const;

/**
 * The wait between probes once the server has stayed unreachable: short, so a page learns of a server that
 * returned within 12 s (10 s and its jitter) with nothing pressed (ADR-078's amendment of 2026-10-04).
 */
export const OFFLINE_PROBE_MS = 10_000;

/**
 * How far a wait is jittered either way, as a share of it.
 */
const JITTER = 0.2;

/**
 * Moves the view by one probe.
 *
 * @param view The view.
 * @param probe What the probe found.
 * @param now The page's clock, in milliseconds.
 * @returns The next view.
 */
export function nextServerView(view: ServerView, probe: Probe, now: number): ServerView {
  if (probe.kind === "acknowledged") {
    return { ...view, restored: false };
  }
  if (probe.kind === "unreachable") {
    const failures = view.failures + 1;
    return { ...view, failures, troubled: view.troubled || failures >= OFFLINE_AFTER_FAILURES, restored: false, notice: probe.notice ?? null };
  }
  if (probe.kind === "no-news") {
    return { ...view, failures: 0, troubled: view.troubled && view.body?.state === "maintenance", restored: view.restored || (view.troubled && view.body?.state !== "maintenance"), notice: null };
  }
  const closed = probe.body.state === "maintenance";
  return {
    failures: 0,
    body: probe.body,
    readAt: now,
    opening: view.opening ?? (closed ? "maintenance" : "other"),
    troubled: closed,
    restored: closed ? false : view.restored || view.troubled,
    notice: null,
  };
}

/**
 * The page time maintenance starts, or null when none is scheduled.
 *
 * @param view The view.
 * @returns The time, or null.
 */
function startsAt(view: ServerView): number | null {
  const schedule = view.body?.maintenance ?? null;
  return schedule === null ? null : view.readAt + schedule.startsInMs;
}

/**
 * The page time maintenance is planned to end, or null when no end was given or none is scheduled.
 *
 * @param view The view.
 * @returns The time, or null.
 */
function endsAt(view: ServerView): number | null {
  const ends = view.body?.maintenance?.endsInMs ?? null;
  return ends === null ? null : view.readAt + ends;
}

/**
 * Where the server stands now.
 *
 * @param view The view.
 * @param now The page's clock.
 * @returns The phase.
 */
export function serverPhase(view: ServerView, now: number): ServerPhase {
  if (view.failures >= OFFLINE_AFTER_FAILURES) {
    return "offline";
  }
  const body = view.body;
  if (body === null) {
    return view.restored ? "restored" : "unknown";
  }
  const start = startsAt(view);
  if (body.state === "maintenance" || (start !== null && now >= start)) {
    return "maintenance";
  }
  if (start !== null) {
    return "soon";
  }
  if (body.state === "busy") {
    return "busy";
  }
  return view.restored ? "restored" : "open";
}

/**
 * What the entry screens do with the page's sound for a phase of the server (P1.12-T12): `hold` while it is under
 * maintenance, `release` once it is anything that is not maintenance and not unknown, `keep` while the page cannot
 * tell (offline, or no answer yet).
 *
 * @param phase The server's phase.
 * @returns What to do with the sound hold.
 */
export function soundHoldFor(phase: ServerPhase): "hold" | "release" | "keep" {
  if (phase === "maintenance") {
    return "hold";
  }
  return phase === "offline" || phase === "unknown" ? "keep" : "release";
}

/**
 * Whether Log in is blocked now: offline, or under maintenance.
 *
 * @param view The view.
 * @param now The page's clock.
 * @returns True while it is.
 */
export function loginBlocked(view: ServerView, now: number): boolean {
  const phase = serverPhase(view, now);
  return phase === "offline" || phase === "maintenance";
}

/**
 * The wait before the next probe: `STEADY_PROBE_MS` while answers come, `MAINTENANCE_PROBE_MS` while the last body
 * read says the server is closed for maintenance, 2, 4 and 8 s after that many probes in a row got none, then
 * `OFFLINE_PROBE_MS`, each jittered by up to a fifth either way so the pages one outage stopped
 * do not return together.
 *
 * @param view The view.
 * @param random A number in [0, 1), `Math.random()` on the page.
 * @returns Milliseconds.
 */
export function probeDelayMs(view: ServerView, random: number): number {
  const steady = view.body?.state === "maintenance" ? MAINTENANCE_PROBE_MS : STEADY_PROBE_MS;
  const base = view.failures === 0 ? steady : (BACKOFF_MS[view.failures - 1] ?? OFFLINE_PROBE_MS);
  return Math.floor(base * (1 - JITTER + 2 * JITTER * random));
}

/**
 * A wait in words: minutes, or hours and minutes from an hour on.
 *
 * @param ms The wait.
 * @returns Such as "25 min" or "1 h 20 min".
 */
export function waitWords(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes < 60) {
    return say("status.wait.minutes", { minutes: String(minutes) });
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? say("status.wait.hours", { hours: String(hours) }) : say("status.wait.hoursMinutes", { hours: String(hours), minutes: String(rest) });
}

/**
 * A planned length in words: minutes to the nearest 5 under an hour, an hour under 90 minutes, else whole hours.
 *
 * @param ms The length.
 * @returns Such as "about 45 min", "about 1 hour" or "about 2 hours".
 */
export function lengthWords(ms: number): string {
  const minutes = ms / 60_000;
  if (minutes < 60) {
    return say("status.length.minutes", { minutes: String(Math.max(5, Math.round(minutes / 5) * 5)) });
  }
  if (minutes < 90) {
    return say("status.length.hour");
  }
  return say("status.length.hours", { hours: String(Math.round(minutes / 60)) });
}

/**
 * The busy strip's sentences: that the world is full and, with an estimate, the wait in the queue window's coarse
 * words (`queueEstimate`), lower case after the colon.
 *
 * @param waitMs The status's `waitMs`, or undefined when the gateway sent none.
 * @returns Such as "The world is full right now. Estimated wait: about 12 min.", or the first sentence alone.
 */
export function busyText(waitMs: number | undefined): string {
  const estimate = waitMs === undefined ? null : queueEstimate(waitMs);
  if (estimate === null) {
    return say("status.busyText");
  }
  return say("status.busyWait", { busy: { k: "status.busyText" }, estimate: `${estimate.charAt(0).toLowerCase()}${estimate.slice(1)}` });
}

/**
 * What the strip shows now.
 *
 * @param view The view.
 * @param now The page's clock.
 * @param clock The time of day of a page time, as the player reads it ("14:00").
 * @returns The strip's tone, title, text, whether it offers the maintenance page's details and whether it offers
 *   Check now (the offline strip alone, where a probe at once is the one thing a player can do), or null for none.
 */
export function serverNotice(view: ServerView, now: number, clock: (ms: number) => string): { readonly tone: ServerNoticeTone; readonly title: string; readonly text: string; readonly details: boolean; readonly check: boolean } | null {
  const phase = serverPhase(view, now);
  if (phase === "offline") {
    return { tone: "offline", title: say("status.offlineTitle"), text: view.notice ?? say("status.offlineText"), details: false, check: true };
  }
  if (phase === "maintenance") {
    const end = endsAt(view);
    const text = end === null ? say("status.noEnd") : end <= now ? say("status.longer") : say("status.backBy", { time: clock(end) });
    return { tone: "maintenance", title: say("status.maintenanceTitle"), text, details: true, check: false };
  }
  if (phase === "soon") {
    const start = startsAt(view) as number;
    const end = endsAt(view);
    const text = end === null ? say("status.soon", { wait: waitWords(start - now) }) : say("status.soonFor", { wait: waitWords(start - now), length: lengthWords(end - start) });
    return { tone: "soon", title: say("status.maintenanceAt", { time: clock(start) }), text, details: false, check: false };
  }
  if (phase === "busy") {
    return { tone: "busy", title: say("status.busyTitle"), text: busyText(view.body?.waitMs), details: false, check: false };
  }
  if (phase === "restored") {
    return { tone: "restored", title: say("status.restoredTitle"), text: say("status.restoredText"), details: false, check: false };
  }
  return null;
}

/**
 * The maintenance page's headline tone: planned, running longer, or open again.
 */
export type MaintenanceTone = "planned" | "longer" | "open";

/**
 * What the maintenance page shows now: its headline and its tone, the sentence under it (none while the server is
 * closed for maintenance, so the headline stands alone), the time strip's line and what is left, and whether the
 * server is open again, which is when the page offers Log in.
 *
 * @param view The view.
 * @param now The page's clock.
 * @param clock The time of day of a page time.
 * @returns The page's content.
 */
export function maintenancePage(view: ServerView, now: number, clock: (ms: number) => string): { readonly tone: MaintenanceTone; readonly title: string; readonly text: string | null; readonly when: string; readonly left: string | null; readonly open: boolean } {
  const phase = serverPhase(view, now);
  if (phase !== "maintenance" && phase !== "offline" && phase !== "unknown") {
    return { tone: "open", title: say("status.openTitle"), text: say("status.openText"), when: say("status.openLabel"), left: say("status.playLabel"), open: true };
  }
  const end = endsAt(view);
  if (end === null) {
    return { tone: "planned", title: say("status.closedTitle"), text: null, when: say("status.noEndLabel"), left: null, open: false };
  }
  if (end <= now) {
    return { tone: "longer", title: say("status.longerTitle"), text: say("status.dueToEnd", { time: clock(end) }), when: say("status.noNewEndLabel"), left: null, open: false };
  }
  return { tone: "planned", title: say("status.closedTitle"), text: null, when: say("status.backByLabel", { time: clock(end) }), left: say("status.inAbout", { wait: waitWords(end - now) }), open: false };
}
